import { useState } from 'react';
import { Form } from 'antd';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useUpdateActionPlanTask } from '@/hooks/v2/useUpdateActionPlanTask';
import { useDeleteActionPlanTask } from '@/hooks/v2/useDeleteActionPlanTask';
import { useUpdateActionPlanTaskStatus } from '@/hooks/v2/useUpdateActionPlanTaskStatus';
import { Task } from '@/components/ActionPlan/types';

interface SelectUser {
	label: string;
	value: string;
}

interface TaskItemProps {
	index: number;
	users: SelectUser[];
}

const { useFormInstance } = Form;

export function useTasksItem({ index, users }: Readonly<TaskItemProps>) {
	const [editing, setEditing] = useState<boolean>(false);

	const form = useFormInstance();
	const taskList = form.getFieldValue('task_list');
	const { organization, company } = useApplicationContext();

	const { mutateAsync: updateTaskStatus } = useUpdateActionPlanTaskStatus();
	const { mutateAsync: updateTask } = useUpdateActionPlanTask();
	const { mutateAsync: deleteTask } = useDeleteActionPlanTask();

	function handleEditTask(data: Task): void {
		form.setFieldsValue({
			action_plan_task_name: data.title,
			action_plan_task_due_date: data.due_date,
			action_plan_task_responsible: data.responsible_user_id
		});

		setEditing(true);
	}

	async function handleCompleteTask(id: string): Promise<void> {
		const payload = {
			organization_id: organization?.id,
			company_id: company?.id,
			action_plan_task_id: id
		};

		await updateTaskStatus(payload);
	}

	async function confirmUpdateTask(): Promise<void> {
		const taskList = form.getFieldValue('task_list');

		const values = form.getFieldsValue([
			'action_plan_task_name',
			'action_plan_task_due_date',
			'action_plan_task_responsible'
		]);

		const currentTask = taskList[index];

		if (currentTask.id) {
			const payload = {
				organization_id: organization?.id,
				company_id: company?.id,
				action_plan_task_id: currentTask.id,
				title: values.action_plan_task_name,
				due_date: values.action_plan_task_due_date,
				responsible_user_id: values.action_plan_task_responsible
			};

			await updateTask(payload);
		} else {
			const newTaskList = taskList.map((task: any, i: number) =>
				i === index
					? {
							...task,
							due_date: values.action_plan_task_due_date,
							responsible_user_id: values.action_plan_task_responsible,
							title: values.action_plan_task_name
					  }
					: task
			);

			form.setFieldValue('task_list', newTaskList);
		}

		setEditing(false);
	}

	function filterUserName(userId?: string | undefined | null): string | undefined {
		if (!userId) {
			return undefined;
		}

		const user = users.find((user: any) => user.value === userId);
		return user?.label;
	}

	async function handleDeleteTask(): Promise<void> {
		const currentTask = taskList[index];

		if (currentTask.id) {
			const payload = {
				organization_id: organization?.id,
				company_id: company?.id,
				action_plan_task_id: currentTask.id
			};

			await deleteTask(payload);
		} else {
			const newTaskList = taskList.filter((_: any, i: number) => i !== index);
			form.setFieldValue('task_list', newTaskList);
		}
	}

	return { form, editing, handleEditTask, handleCompleteTask, confirmUpdateTask, filterUserName, handleDeleteTask };
}
